import React, { useEffect, useState, useRef } from 'react';

import { useParams, useNavigate } from 'react-router-dom';
import { Label, Hint } from '@progress/kendo-react-labels';
import { ErrorBoundary } from 'react-error-boundary';
import { ErrorFallback, MessageAlert, Loader, TextInput } from 'smart-react';
import { Card, CardBody, CardHeader } from '@progress/kendo-react-layout';
import { Button } from '@progress/kendo-react-buttons';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Form, Field } from '@progress/kendo-react-form';
import { Switch, TextArea } from '@progress/kendo-react-inputs';
import './CreateRollout.scss';
import { MultiSelect } from '@progress/kendo-react-dropdowns';
import CreateRolloutLoader from '../Components/CreateRolloutLoader/CreateRolloutLoader';

import { useAuth } from '../../Core/Context/AuthContext';
import {
  getAllCauses,
  getRolloutName,
  saveRollout,
} from '../Services/ProjectService';
import { filterBy } from '@progress/kendo-data-query';

let bugData = [];

/**
 * Create Rollout main screen
 * @type {React.FC<Props>}
 * @returns {React.ReactElement} The CreateRollout component.
 */
const CreateRollout = () => {
  const { projectId, versionId, action, major, name } = useParams();
  const { configData } = useAuth();
  const navigate = useNavigate();
    // Ref for MultiSelect input
  const multiSelectRef = useRef();
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isBugsLoading, setIsBugsLoading] = useState(false);
  const [bugs, setBugs] = useState([]);
  const [rolloutVersion, setRolloutVersion] = useState('');
  const [rolloutName, setRolloutName] = useState('');
  const [oldRolloutName, setOldRolloutName] = useState('');
  const [platform, setPlatform] = useState(configData?.DOWNLOAD_PLATFORM);
  const [selectedBugs, setSelectedBugs] = useState([]);
  const [rolloutDescription, setRolloutDescription] = useState('');
  const [noBugsWarning, setNoBugsWarning] = useState('');
  const [alertMessage, setAlertMessage] = React.useState({
    alert: false,
    message: null,
  });
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  const [warningText, setWarningText] = useState('');
  const [postNavPayload, setPostNavPayload] = useState(null);

  /**
   * used to filter the Bugs
   * @param {*} event
   */
  const filterChange = (event) => {
    setBugs(filterBy(bugData.slice(), event.filter));
  };
  /**
   * Description: call api for getting rollout name
   * @return
   */
  const fetchRolloutName = async () => {
    setIsPageLoading(true);
    try {
      const response = await getRolloutName(
        projectId,
        versionId,
        action,
        major,
        name
      );
      if (response?.isSuccess) {
        let rolloutData = response?.payload;
        let rolloutNameDetail = rolloutData?.name;
        let rollout = rolloutNameDetail.split('--');
        setRolloutName(rollout[0].replace(/\s+/g, ''));
        setOldRolloutName(rollout[0].replace(/\s+/g, ''));
        setRolloutVersion(rollout[1].replace(/\s+/g, ''));
      } else {
        setAlertMessage({
          alert: true,
          message: response?.message,
        });
      }
    } catch (error) {
      setAlertMessage({
        alert: true,
        message: { error },
      });
    } finally {
      setIsPageLoading(false);
    }
  };



  // Paste handler for MultiSelect to allow comma-separated pasting
  useEffect(() => {
    const input = multiSelectRef.current?.element?.querySelector('input');
    if (input) {
      input.onpaste = handlePaste;
    }
    // Cleanup
    return () => {
      if (input) input.onpaste = null;
    };
  }, [bugs]);

  /**
   * Handles comma, newline, or tab-separated pasting into MultiSelect
   * and updates selectedBugs state.
   */
  const handlePaste = (e) => {
    e.preventDefault();
    const paste = (e.clipboardData || window.clipboardData).getData('text');
    if (!paste) return;

    const splitValues = paste
      .split(/[\s,\n\t;]+/)
      .map((v) => v.trim())
      .filter(Boolean);

    const matchedOrCustom = splitValues.map((val) => {
      let found = bugs.find((b) => b.value === val || b.label === val);
      return found || { value: val, label: val };
    });

    // Merge with existing selections; prevent duplicates
    setSelectedBugs((prev) => [
      ...prev,
      ...matchedOrCustom.filter((v) => !prev.some((sel) => sel.value === v.value)),
    ]);
  };
  /**
   * Description: call api for getting rollout bugs
   * @return
   */
  const fetchRolloutBugs = async () => {
    setIsBugsLoading(true);
    try {
      const response = await getAllCauses(projectId, versionId, action, name);

      if (response?.isSuccess) {
        if (response?.payload?.length === 0) {
          setNoBugsWarning(
            response?.status?.message ||
              'No bugs found for the selected version'
          );
          bugData = [];
          setBugs([]);
        } else {
          setNoBugsWarning('');
          bugData = setBugsLabels(response?.payload);
          setBugs(bugData);
        }
      } else {
        setAlertMessage({
          alert: true,
          message: response?.message,
        });
      }
    } catch (error) {
      setAlertMessage({
        alert: true,
        message: { error },
      });
    } finally {
      setIsBugsLoading(false);
    }
  };

  /**
   * Description: create bug label object array from arr
   * @param arr array
   * @return objects array
   */
  const setBugsLabels = (arr) => {
    const bugArr = arr?.map((item) => ({
      value: `${item?.key}`,
      label: `${item?.key}`,
    }));
    return bugArr;
  };

  /**
   * Description: check platform its linux or not
   * @return boolean
   */
  const checkPlatform = () => (platform === 'tar' ? true : false);

  /**
   * Description: handle when platform changed
   * @return
   */
  const platformChanged = (event) => {
    setPlatform(event.value ? 'tar' : 'zip');
  };

  /**
   * Description: handle bug change and set rollout name
   *  @param bugList array
   */
  const handleBugsChange = () => {
    if (selectedBugs?.length > 0) {
      const bugId =
        selectedBugs?.length > 1
          ? 'ACCUM'
          : (selectedBugs[0]?.value ?? selectedBugs[0]?.label);
      setRolloutName(oldRolloutName.replace('{BUGID}', bugId));
    } else {
      setRolloutName(oldRolloutName);
    }
  };
  const convertBugsArray = (dataArray) => {
    return dataArray?.map((item) => item?.value ?? item?.label);
  };
  /**
   * Handle form input changes
   */
  const handleRolloutDescription = (event) => {
    setRolloutDescription(event.target.value);
  };

  const handleRolloutNameChange = (event) => {
    const value = event?.value ?? event?.target?.value ?? '';
    console.log('rolloutName', value);
    setRolloutName(value);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    const formData = {
      rolloutProduct: projectId,
      rolloutReleaseVersion: versionId,
      rolloutReleaseVersionName: '',
      rolloutVersion: rolloutVersion,
      rolloutName: rolloutName,
      bugs: convertBugsArray(selectedBugs),
      platform: platform === 'linux' ? 'linux' : '',
      rolloutDescription: rolloutDescription,
    };
    try {
      const response = await saveRollout(formData);
      if (response.isSuccess) {
        const payload = response?.payload || {};
        const hasWarning = Boolean(payload?.warning) || (Array.isArray(payload?.missingBugs) && payload.missingBugs.length > 0);
        if (hasWarning) {
          const baseText = payload.warning || 'Some bugs had no committed files and were not included.';
          const missingList = Array.isArray(payload.missingBugs) && payload.missingBugs.length > 0
            ? ` Missing: ${payload.missingBugs.join(', ')}`
            : '';
          setWarningText(`${baseText}${missingList}`);
          setPostNavPayload({
            platform: payload.platform,
            rolloutName: payload.rolloutName,
          });
          setShowWarningDialog(true);
        } else {
          navigate(`/rolloutList/${projectId}/${versionId}`, {
            state: {
              downloadRolloutStatus: true,
              rolloutPlatform: payload.platform,
              downloadRolloutName: payload.rolloutName,
            },
          });
        }
      } else {
        setAlertMessage({
          alert: true,
          message: response?.message,
        });
      }
    } catch (error) {
      setAlertMessage({
        alert: true,
        message: 'Error submitting form data:'.error,
      });
      // Handle error here
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWarningContinue = () => {
    setShowWarningDialog(false);
    if (postNavPayload) {
      navigate(`/rolloutList/${projectId}/${versionId}`, {
        state: {
          downloadRolloutStatus: true,
          rolloutPlatform: postNavPayload.platform,
          downloadRolloutName: postNavPayload.rolloutName,
        },
      });
    }
  };

  useEffect(() => {
    if (
      bugs &&
      bugs[0]?.value &&
      (action === 'recreate' || action === 'accum')
    ) {
      const values = bugs?.map((item) => ({
        value: `${item?.value}`,
        label: ` ${item?.label} `,
      }));
      setSelectedBugs(values);
    }
  }, [bugs]);

  useEffect(() => {
    fetchRolloutName();
    fetchRolloutBugs();
  }, []);

  useEffect(() => {
    handleBugsChange();
  }, [selectedBugs]);

  useEffect(() => {
    setPlatform(configData?.DOWNLOAD_PLATFORM);
  }, [configData]);

  const handleCancel = () => {
    navigate(-1); // This will take the user to the previous page in the history
  };

  const onChange = React.useCallback((event) => {
    setSelectedBugs(event.value); // Set all selected items
  }, []);

  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={() => {
        // reset the state of your app so the error doesn't happen again
      }}
    >
      <React.Suspense fallback={<Loader />}>
        {isPageLoading ? (
          <CreateRolloutLoader />
        ) : (
        <div
          className={`container mx-auto rollout-form-container ${
            isSubmitting ? 'is-submitting' : ''
          }`}
        >
          {showWarningDialog && (
            <Dialog
              title={<span className="k-text-warning">Rollout Warning</span>}
              onClose={handleWarningContinue}
            >
              <div className="k-mb-3">
                <MessageAlert
                  type={'warning'}
                  message={warningText}
                  isIcon={true}
                  iconName={'warning'}
                />
              </div>
              <DialogActionsBar>
                <Button themeColor="warning" onClick={handleWarningContinue}>
                  Continue
                </Button>
              </DialogActionsBar>
            </Dialog>
          )}
          {isSubmitting && (
            <div className="create-rollout-submit-loader">
              <Loader />
            </div>
          )}
          <div className="container add-page-section">
            {alertMessage?.alert && (
              <MessageAlert
                type={'danger'}
                message={alertMessage.message}
                isIcon={true}
                iconName={'error'}
              />
            )}

            <Form
              onSubmit={handleSubmit}
              render={() => (
                <>
                  <Card className="k-mt-3 detail-card">
                    <CardBody className="detail-card-body">
                      <div className="card-content rollout-form-card">
                        <div className="account-form-wrapper">
                          <div className="k-d-flex single-field-row single-row-100">
                            <div className="k-p-2 field-wrapper k-w-100">
                              <Field
                                key={'rolloutName'}
                                id={'rolloutName'}
                                name={'rolloutName'}
                                label={'Rollout Name'}
                                data={rolloutName}
                                onChange={handleRolloutNameChange}
                                type={'text'}
                                component={TextInput}
                                optional={false}
                                requiredField={true}
                              />
                            </div>
                            <div className="k-p-2 field-wrapper k-w-100">
                              <Field
                                key={'rolloutVersion'}
                                id={'rolloutVersion'}
                                name={'rolloutVersion'}
                                label={'Version'}
                                data={rolloutVersion}
                                type={'text'}
                                component={TextInput}
                                optional={false}
                                requiredField={true}
                              />
                            </div>
                          </div>
                          <div className="k-d-flex single-field-row single-row-100">
                            <div className="k-p-2 field-wrapper k-w-100">
                              <Label editorId={'project'}>
                                Bugs List{' '}
                                <span className="required-field">*</span>
                              </Label>
                              <MultiSelect
                                ref={multiSelectRef}
                                textField="label"
                                dataItemKey="value"
                                name="version"
                                id="version"
                                placeholder="Bugs"
                                filterable={true}
                                onFilterChange={filterChange}
                                loading={isBugsLoading}
                                className="smart-multiselect k-align-self-end"
                                data={bugs}
                                required={true}
                                value={selectedBugs}
                                onChange={onChange}
                                allowCustom={true}
                              />
                              {noBugsWarning && (
                                <div className="k-mt-2">
                                  <Hint
                                    id={'noBugWarning'}
                                    className="k-text-warning"
                                  >
                                    {noBugsWarning}
                                  </Hint>
                                </div>
                              )}
                            </div>
                            <div className="k-p-2 field-wrapper k-w-100">
                              <Label editorId={'rolloutDescription'}>
                                Platform{' '}
                                <span className="required-field">*</span>
                              </Label>
                              <div className="select-platform k-mt-4">
                                <span className="toggle-label">Zip</span>
                                <Switch
                                  onLabel="tar"
                                  offLabel="zip"
                                  id="platform"
                                  name="platform"
                                  checked={checkPlatform()}
                                  onChange={platformChanged}
                                  size="large"
                                />
                                <span className="toggle-label">Tar</span>
                              </div>
                            </div>
                          </div>
                          <div className="k-d-flex single-field-row single-row-100">
                            <div className="k-p-2 field-wrapper k-w-100">
                              <Label editorId={'rolloutDescription'}>
                                Description{' '}
                                <span className="required-field">*</span>
                              </Label>
                              <TextArea
                                id={'rolloutDescription'}
                                name={'rolloutDescription'}
                                onChange={handleRolloutDescription}
                                rows={5}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                  <Card className="k-mt-3">
                    <CardHeader>
                      <div className="k-d-flex single-field-row button-form-wrapper k-action-buttons !k-px-0 k-float-right">
                        <>
                          <Button
                            icon={'cancel'}
                            type="button"
                            onClick={handleCancel}
                            disabled={isSubmitting}
                          >
                            Cancel
                          </Button>
                          <Button
                            text="save"
                            themeColor={'primary'}
                            onClick={handleSubmit}
                            disabled={
                              isSubmitting ||
                              rolloutName.trim() === '' ||
                              rolloutVersion.trim() === '' ||
                              selectedBugs.length === 0
                            }
                          >
                            {isSubmitting ? 'Creating' : 'Create Rollout'}
                          </Button>
                        </>
                      </div>
                    </CardHeader>
                  </Card>
                </>
              )}
            />
          </div>
        </div>
        )}
      </React.Suspense>
    </ErrorBoundary>
  );
};
export default CreateRollout;
